import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateEntitlementGroupInterface,
  EntitlementGroupInterface,
  UpdateEntitlementGroupInterface
} from '@thro-types/access-handler';

import { AxiosRequestConfig } from 'axios';

import {
  createEntitlementGroup,
  deleteEntitlementGroup,
  getEntitlementGroups,
  updateEntitlementGroup
} from 'api/accessHandlerService';

export const useEntitlementGroupsQuery = (enabled = true, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['entitlementGroups', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const entitlementGroups = getEntitlementGroups(config);
      return entitlementGroups;
    },
    placeholderData: keepPreviousData,
    enabled
  });

  return query;
};

export const useEntitlementGroupMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['entitlementGroups'];

  const create = useMutation<EntitlementGroupInterface, Error, CreateEntitlementGroupInterface>({
    mutationFn: async args => {
      return await createEntitlementGroup(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<
    EntitlementGroupInterface,
    Error,
    { id: number; data: UpdateEntitlementGroupInterface }
  >({
    mutationFn: async args => {
      return await updateEntitlementGroup(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<EntitlementGroupInterface, Error, number>({
    mutationFn: async args => {
      return await deleteEntitlementGroup(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
